import React, { useCallback, useEffect, useState } from 'react';
import {
  ShoppingCart,
  Menu,
  Search,
  Share2,
  X
} from 'lucide-react';
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaUser,
  FaYoutube
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EducationDomainFooter from '../views/LandingPage/EducationDomain/EducationDomainFooter.jsx';
import EducationDomainHeader from '../views/LandingPage/EducationDomain/EducationDomainHeader.jsx';
import { useCart } from '../context/CartContext.jsx';
import { useSiteContext } from '../context/SiteContext.jsx';
import {
  getCompanyLogoUrl,
  getCompanyPolicyLinks,
  getTemplateCompanyContext,
  selectTemplateCompanyState
} from './TemplateCompanyUtils.js';
import { getStoredPublishedActiveTemplate, normalizeTemplateForReader } from './TemplateStorage.jsx';
import { getTemplateAccountRoute, isTemplateAuthRoute, useTemplateAuthState } from './TemplateAuthState.jsx';
import { useAuthSession } from '../auth/useAuthSession.js';
import TemplateRouterLink from './TemplateRouterLink.jsx';
import { resolveFooterTokens } from './footerTokens.js';
import { linkProps } from './templateLinkConfig.js';
import { resolveHeaderBackground, resolveHeaderTypography, resolveHeaderNavStyle } from './headerTokens.js';
import { isEducationExamTemplate } from './EducationTemplateVariant.js';
import {
  DEFAULTS as EXAM_CHROME_DEFAULTS,
  EXAM_HOME_CSS,
  ExamFooter,
  ExamNav,
  resolveExamAuth,
} from './sections/education/EducationExamHomeSection.jsx';

const themeOf = (template) => template?.attributes?.theme || {};

const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};

const resolveHeaderPositionMode = (header = {}) => {
  if (['normal', 'sticky', 'fixed', 'overlay'].includes(header.positionMode)) return header.positionMode;
  if (header.overlay === true || header.headerTheme === 'transparent') return 'overlay';
  return header.sticky === false ? 'normal' : 'sticky';
};

const isTransparentHeader = (header = {}) => (
  header.headerTheme === 'transparent'
  || header.backgroundColor === 'transparent'
  || header.transparent === true
  || resolveHeaderPositionMode(header) === 'overlay'
);

const educationSocialIconMap = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  twitter: FaTwitter,
  youtube: FaYoutube
};

const educationDefaultTheme = {
  primary: '#3B9394',
  secondary: '#746EAC',
  accent: '#2563eb'
};

const educationDefaultHeader = {
  height: '70px',
  backgroundColor: '#ffffff',
  borderColor: '#f1f5f9',
  shadow: '0 2px 10px rgba(0,0,0,0.08)',
  logoWidth: '100px',
  navAlignment: 'left',
  searchPlaceholder: 'Search courses, categories...',
  searchButtonLabel: 'Search',
  cartLabel: 'Cart',
  signInLabel: 'Sign In'
};

const compact = (values = []) => values.find((value) => value !== undefined && value !== null && value !== '');

const normalizedValue = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');

const differsFromDefault = (value, defaultValue) => {
  if (value === undefined || value === null || value === '') return false;
  return normalizedValue(value) !== normalizedValue(defaultValue);
};

const hasCustomizedEducationChrome = (template, header = {}, theme = {}) => {
  if (header.mode === 'custom') return true;
  if (template?.attributes?.runtime?.renderMode !== 'legacy-parity') return true;

  return [
    ['primary', theme.primary, educationDefaultTheme.primary],
    ['secondary', theme.secondary, educationDefaultTheme.secondary],
    ['accent', theme.accent, educationDefaultTheme.accent],
    ['height', header.height, educationDefaultHeader.height],
    ['backgroundColor', header.backgroundColor, educationDefaultHeader.backgroundColor],
    ['backgroundGradient', header.backgroundGradient, ''],
    ['borderColor', header.borderColor, educationDefaultHeader.borderColor],
    ['borderWidth', header.borderWidth, '1px'],
    ['shadow', header.shadow, educationDefaultHeader.shadow],
    ['showSeparatorLine', header.showSeparatorLine, true],
    ['logoWidth', header.logoWidth, educationDefaultHeader.logoWidth],
    ['navAlignment', header.navAlignment, educationDefaultHeader.navAlignment],
    ['searchPlaceholder', header.searchPlaceholder, educationDefaultHeader.searchPlaceholder],
    ['searchButtonLabel', header.searchButtonLabel, educationDefaultHeader.searchButtonLabel],
    ['searchWidth', header.searchWidth, '400px'],
    ['cartLabel', header.cartLabel, educationDefaultHeader.cartLabel],
    ['signInLabel', header.signInLabel, educationDefaultHeader.signInLabel],
    ['ctaLabel', header.ctaLabel, ''],
    ['ctaGradient', header.ctaGradient, ''],
    ['ctaStyle', header.ctaStyle, 'solid'],
    ['ctaUrl', header.ctaUrl, ''],
    ['customCss', header.customCss, ''],
    ['customHtml', header.customHtml, '']
  ].some(([, value, defaultValue]) => differsFromDefault(value, defaultValue));
};

const resolveCtaStyle = (header = {}, theme = {}) => {
  const primary = compact([header.ctaColor, header.buttonColor, theme.primary, educationDefaultTheme.primary]);
  const fill = compact([header.ctaGradient, header.ctaBackground, header.buttonBackground, primary]);
  const textColor = compact([header.ctaTextColor, header.buttonTextColor, '#ffffff']);
  const ctaStyle = header.ctaStyle || 'solid';

  if (ctaStyle === 'outline') {
    return {
      background: 'transparent',
      color: compact([header.ctaTextColor, primary]),
      border: `1px solid ${primary}`
    };
  }

  if (ctaStyle === 'ghost') {
    return {
      background: 'transparent',
      color: compact([header.ctaTextColor, primary]),
      border: '1px solid transparent'
    };
  }

  return {
    background: fill,
    color: textColor,
    border: '1px solid transparent'
  };
};

const resolveActiveEducationTemplate = (siteContext = null) => {
  if (siteContext?.templateResolution) {
    if (siteContext.templateResolution.status !== 'assigned' || !siteContext.activeTemplate) return null;
    return normalizeTemplateForReader(siteContext.activeTemplate, 'EDUCATION');
  }

  const storedTemplate = getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: 'EDUCATION'
  });
  return storedTemplate ? normalizeTemplateForReader(storedTemplate, 'EDUCATION') : null;
};

const useActiveEducationTemplate = (siteContext = null) => {
  const [template, setTemplate] = useState(() => resolveActiveEducationTemplate(siteContext));

  useEffect(() => {
    const refresh = () => setTemplate(resolveActiveEducationTemplate(siteContext));
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
    };
  }, [siteContext]);

  return template;
};

/**
 * Maps the template's authored header nav onto the shape the Exam nav expects, falling back to
 * the approved design's own entries when nothing is authored.
 */
const examChromeContent = (template) => {
  const header = template?.attributes?.header || {};
  const footer = template?.attributes?.footer || {};
  const authoredNav = Array.isArray(header.nav)
    ? header.nav
      .filter((item) => item && item.visible !== false)
      .map((item) => {
        const url = item.url || item.route || item.href;
        const label = item.label || item.title;
        const isCourseEntry = String(url || '').split('#')[0].replace(/\/$/, '') === '/course';
        const isCourseLabel = ['course', 'courses'].includes(String(label || '').trim().toLowerCase());
        return { label: isCourseEntry && isCourseLabel ? 'Exams' : label, url };
      })
      .filter((item) => item.label && item.url)
    : [];

  return {
    ...EXAM_CHROME_DEFAULTS,
    ...(authoredNav.length ? { nav: authoredNav } : {}),
    ...(header.signInLabel ? { signInLabel: header.signInLabel } : {}),
    ...(header.ctaLabel ? { registerLabel: header.ctaLabel } : {}),
    ...(footer.copyright ? { footerCopyright: footer.copyright } : {})
  };
};

/**
 * Exam Portal nav rendered as standalone site chrome, on its own gradient band.
 *
 * `onNavigate` routes internal links through the router. This chrome lives OUTSIDE
 * TemplateRenderer (Layout renders it), so it cannot rely on TemplateRenderer's anchor-click
 * interception — without it every nav click would reload the whole document.
 */
const ExamChromeHeader = ({ template, auth }) => {
  const navigate = useNavigate();
  const accountRoute = getTemplateAccountRoute('EDUCATION');
  const content = examChromeContent(template);
  const handleNavigate = useCallback((href) => navigate(href), [navigate]);

  return (
    <div className="aae-home" data-education-exam-chrome="header">
      <style>{EXAM_HOME_CSS}</style>
      <div className="aae-chrome-bar">
        <ExamNav content={content} auth={{ ...auth, accountRoute }} onNavigate={handleNavigate} />
      </div>
    </div>
  );
};

/** Exam Portal footer rendered as standalone site chrome. */
const ExamChromeFooter = ({ template, company }) => {
  const navigate = useNavigate();
  const handleNavigate = useCallback((href) => navigate(href), [navigate]);

  return (
    <div className="aae-home aae-chrome-footer" data-education-exam-chrome="footer">
      <style>{EXAM_HOME_CSS}</style>
      <ExamFooter content={examChromeContent(template)} company={company} onNavigate={handleNavigate} />
    </div>
  );
};

export const ConfigurableEducationHeader = ({ template: providedTemplate, fallback = true }) => {
  const navigate = useNavigate();
  const templateAuth = useTemplateAuthState();
  const session = useAuthSession();
  const auth = resolveExamAuth(templateAuth, {
    isLoggedIn: session.isAuthenticated || session.isLoggedIn
  });
  const cart = useCart() || {};
  const siteContext = useSiteContext();
  const reduxCompanyState = useSelector(selectTemplateCompanyState);
  const companyState = siteContext?.company || reduxCompanyState;
  const storedTemplate = useActiveEducationTemplate(siteContext);
  const template = providedTemplate || storedTemplate;
  const header = template?.attributes?.header;
  const theme = themeOf(template);
  const [searchValue, setSearchValue] = useState('');
  const [openMenu, setOpenMenu] = useState(false);

  // Exam template: use the Exam Portal's own nav on EVERY education route. This must come
  // before the legacy/legacy-parity fallbacks below, because normalizeEducationTemplate stamps
  // `header.mode: 'legacy'`, which would otherwise return the DEFAULT EducationDomainHeader on
  // all the functional routes (attendance, myCourses, profile, exam-management, …).
  if (isEducationExamTemplate(template)) {
    return <ExamChromeHeader template={template} auth={auth} />;
  }

  if (!header) {
    return fallback ? <EducationDomainHeader /> : null;
  }
  if (header.visible === false) {
    return fallback ? <EducationDomainHeader /> : null;
  }
  if (header.mode === 'legacy' && !hasCustomizedEducationChrome(template, header, theme)) {
    return fallback ? <EducationDomainHeader /> : null;
  }
  if (template?.attributes?.runtime?.renderMode === 'legacy-parity' && !hasCustomizedEducationChrome(template, header, theme)) {
    return fallback ? <EducationDomainHeader /> : null;
  }

  const nav = Array.isArray(header.nav)
    ? header.nav.filter((item) => item.visible !== false).filter((item) => !auth.isLoggedIn || !isTemplateAuthRoute(item))
    : [];
  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const logo = getCompanyLogoUrl(companyContext) || assetUrl(header.logoContent, header.logoUrl);
  const currentPath = window.location.pathname;
  const navAlignment = header.navAlignment || 'left';
  const accountRoute = getTemplateAccountRoute('EDUCATION');
  const storedCartCount = (() => {
    try {
      const items = JSON.parse(localStorage.getItem('cartProducts') || '[]');
      return Array.isArray(items) ? items.reduce((total, item) => total + Number(item?.quantity || 1), 0) : 0;
    } catch {
      return 0;
    }
  })();
  const cartItemCount = Number(cart.total_items || storedCartCount || 0);
  const handleMobileNavigation = () => setOpenMenu(false);
  const handleSearch = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const search = new FormData(form).get('search');
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    setOpenMenu(false);
    navigate(`/course${params}`);
  };
  const clearSearch = () => setSearchValue('');
  const positionMode = resolveHeaderPositionMode(header);
  const transparentHeader = isTransparentHeader(header);
  const fixedHeader = positionMode === 'fixed';
  const overlayHeader = positionMode === 'overlay';
  const headerHeight = header.height || theme.headerHeight || '70px';
  // Task 8.3: resolved header customization (additive — unset reproduces today's look).
  const headerBackgroundTokens = resolveHeaderBackground(theme, header || {});
  const headerTypography = resolveHeaderTypography(theme, header || {});
  const headerNavStyle = resolveHeaderNavStyle(theme, header || {});
  const headerBackgroundOverride = headerBackgroundTokens.mode === 'solid'
    && header && header.background && (header.background.color || header.background.mode === 'solid')
    ? headerBackgroundTokens.backgroundColor
    : null;
  const headerBackground = transparentHeader
    ? (header.backgroundGradient || 'transparent')
    : (headerBackgroundOverride || header.backgroundGradient || header.backgroundColor || '#ffffff');
  const navSpacing = headerNavStyle.spacing != null ? headerNavStyle.spacing : 8;
  const primary = compact([theme.primary, educationDefaultTheme.primary]);
  const secondary = compact([theme.secondary, educationDefaultTheme.secondary]);
  const navColor = compact([header.navColor, header.linkColor, secondary]);
  const iconColor = compact([header.iconColor, header.cartColor, navColor]);
  const searchWidth = header.searchWidth || 400;
  const searchBorderColor = header.searchBorderColor || header.borderColor || '#e5e7eb';
  const searchTextColor = header.searchTextColor || theme.color || '#374151';
  const searchMutedColor = header.searchMutedColor || theme.mutedColor || '#9ca3af';
  const searchButtonStyle = resolveCtaStyle({
    ...header,
    ctaGradient: header.searchButtonGradient || header.searchButtonBackground || header.ctaGradient,
    ctaColor: header.searchButtonColor || header.ctaColor,
    ctaTextColor: header.searchIconColor || header.searchButtonTextColor || header.ctaTextColor
  }, theme);
  const ctaStyle = resolveCtaStyle(header, theme);
  const activeNavBackground = header.activeNavBackground || '#f3f4f6';
  const separatorBorder = header.showSeparatorLine === false
    ? '0 solid transparent'
    : `${header.borderWidth || '1px'} solid ${transparentHeader ? (header.borderColor || 'transparent') : (header.borderColor || '#f1f5f9')}`;

  return (
    <>
    <header
      id="education-template-header"
      style={{
        width: '100%',
        background: headerBackground,
        height: headerHeight,
        boxShadow: transparentHeader ? (header.shadow || 'none') : (header.shadow || '0 2px 10px rgba(0, 0, 0, 0.08)'),
        borderBottom: separatorBorder,
        position: fixedHeader ? 'fixed' : overlayHeader ? 'absolute' : positionMode === 'normal' ? 'relative' : 'sticky',
        top: positionMode === 'normal' ? 'auto' : 'env(safe-area-inset-top, 0px)',
        left: fixedHeader || overlayHeader ? 0 : undefined,
        right: fixedHeader || overlayHeader ? 0 : undefined,
        zIndex: 9999,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: navAlignment === 'center' ? 'center' : navAlignment === 'right' ? 'flex-end' : 'space-between',
          alignItems: 'center',
          height: '100%',
          maxWidth: 1300,
          margin: '0 auto',
          padding: '0 20px',
          gap: 16
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexShrink: 0 }}>
          {logo ? (
            <TemplateRouterLink to="/" style={{ display: 'inline-flex' }}>
              <img src={logo} alt={header.logoAlt || 'Logo'} style={{ width: header.logoWidth || 100, height: 'auto', objectFit: 'contain' }} />
            </TemplateRouterLink>
          ) : (
          <TemplateRouterLink to="/" style={{ color: headerTypography.color || secondary, fontWeight: headerTypography.fontWeight || 800, fontFamily: headerTypography.fontFamily, fontSize: headerTypography.fontSize, letterSpacing: headerTypography.letterSpacing, textDecoration: 'none' }}>AVPS</TemplateRouterLink>
          )}
          <nav className="education-template-header-nav" style={{ display: 'flex', alignItems: 'center', gap: navSpacing }}>
            {nav.map((item) => (
              <TemplateRouterLink
                key={`${item.label}-${item.route}`}
                {...linkProps(item.link, item.route)}
                style={{
                  color: item.color || headerNavStyle.linkColor || navColor,
                  fontFamily: headerNavStyle.fontFamily,
                  fontWeight: headerNavStyle.fontWeight != null ? headerNavStyle.fontWeight : 600,
                  fontSize: headerNavStyle.fontSize != null ? headerNavStyle.fontSize : 14,
                  padding: '6px 12px',
                  borderRadius: 6,
                  textDecoration: 'none',
                  background: currentPath === item.route ? activeNavBackground : 'transparent'
                }}
              >
                {item.label}
              </TemplateRouterLink>
            ))}
          </nav>
        </div>
        {header.showSearch !== false && (
        <div className="education-template-header-search" style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: searchWidth }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', border: `1px solid ${searchBorderColor}`, borderRadius: 8, background: header.searchBackground || '#fff', paddingLeft: 12 }}>
            <input
              name="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={header.searchPlaceholder || 'Search courses, categories...'}
              style={{ flex: 1, border: 0, outline: 0, padding: '10px 8px', fontSize: 14, color: searchTextColor, minWidth: 0, background: 'transparent' }}
            />
            {searchValue && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                style={{ border: 0, background: 'transparent', color: searchMutedColor, display: 'grid', placeItems: 'center', width: 30, height: 30, cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            )}
            <button
              type="submit"
              aria-label={header.searchButtonLabel || 'Search'}
              style={{ margin: 4, border: searchButtonStyle.border, borderRadius: 6, background: searchButtonStyle.background, color: searchButtonStyle.color, width: 34, height: 32, display: 'grid', placeItems: 'center', cursor: 'pointer' }}
            >
              <Search size={18} />
            </button>
          </form>
        </div>
        )}
        <div className="education-template-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {header.showCart !== false && (
          <TemplateRouterLink to="/cart" aria-label={header.cartLabel || 'Cart'} style={{ color: iconColor, fontWeight: 700, textDecoration: 'none', position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ShoppingCart size={22} />
            {cartItemCount > 0 && (
              <span style={{ position: 'absolute', top: -8, right: -10, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: primary, color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center', fontWeight: 800 }}>
                {cartItemCount}
              </span>
            )}
          </TemplateRouterLink>
          )}
          {auth.isLoggedIn ? (
            <TemplateRouterLink to={accountRoute} aria-label="Profile" title="Profile" style={{ background: ctaStyle.background, color: ctaStyle.color, border: ctaStyle.border, fontWeight: 700, borderRadius: 8, padding: '9px 14px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
              <FaUser size={16} />
              <span>Profile</span>
            </TemplateRouterLink>
          ) : (header.showContactButton !== false && header.hideCta !== true) ? (
            <TemplateRouterLink to={header.ctaUrl || '/sign-in'} style={{ background: ctaStyle.background, color: ctaStyle.color, border: ctaStyle.border, fontWeight: 700, borderRadius: 8, padding: '9px 16px', textDecoration: 'none' }}>
              {header.ctaLabel || header.signInLabel || 'Sign In'}
            </TemplateRouterLink>
          ) : null}
        </div>
        <button
          type="button"
          className="education-template-mobile-menu-button"
          onClick={() => setOpenMenu((value) => !value)}
          aria-label="Toggle menu"
          style={{ display: 'none', border: `1px solid ${header.borderColor || '#e5e7eb'}`, background: header.mobileMenuButtonBackground || '#f8fafc', color: iconColor, borderRadius: 6, width: 40, height: 40, placeItems: 'center', flexShrink: 0, cursor: 'pointer' }}
        >
          <Menu size={22} />
        </button>
      </div>
      {openMenu && (
        <div className="education-template-mobile-menu" role="presentation" style={{ display: 'none' }}>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpenMenu(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 119, border: 0, background: 'rgba(15, 23, 42, 0.22)', cursor: 'default' }}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 120,
              width: 280,
              maxWidth: '86vw',
              background: '#ffffff',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
              padding: 16,
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <TemplateRouterLink to="/" onClick={handleMobileNavigation} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 40, color: navColor, fontWeight: 800, textDecoration: 'none' }}>
                {logo ? (
                  <img src={logo} alt={header.logoAlt || 'Logo'} style={{ width: 90, height: 'auto', objectFit: 'contain' }} />
                ) : (
                  header.brandName || 'AVPS'
                )}
              </TemplateRouterLink>
              <button
                type="button"
                onClick={() => setOpenMenu(false)}
                aria-label="Close mobile navigation"
                style={{ width: 34, height: 34, borderRadius: 6, border: 0, background: activeNavBackground, color: iconColor, display: 'grid', placeItems: 'center', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
            {nav.map((item) => (
              <TemplateRouterLink key={`${item.label}-${item.route}-mobile`} {...linkProps(item.link, item.route)} onClick={handleMobileNavigation} style={{ color: item.color || headerNavStyle.linkColor || navColor, fontFamily: headerNavStyle.fontFamily, fontWeight: 700, textDecoration: 'none', padding: '10px 14px', borderRadius: 6, background: currentPath === item.route ? activeNavBackground : 'transparent' }}>
                {item.label}
              </TemplateRouterLink>
            ))}
            {header.showCart !== false && (
            <TemplateRouterLink to="/cart" onClick={handleMobileNavigation} style={{ color: iconColor, fontWeight: 700, textDecoration: 'none', padding: '10px 14px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 12, background: currentPath === '/cart' ? activeNavBackground : 'transparent' }}>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <ShoppingCart size={20} />
                {cartItemCount > 0 && (
                  <span style={{ position: 'absolute', top: -9, right: -11, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: primary, color: '#fff', fontSize: 10, lineHeight: '18px', textAlign: 'center', fontWeight: 800, border: '2px solid #ffffff' }}>
                    {cartItemCount}
                  </span>
                )}
              </span>
              <span>{header.cartLabel || 'Cart'}</span>
            </TemplateRouterLink>
            )}
            {(auth.isLoggedIn || (header.showContactButton !== false && header.hideCta !== true)) && (
            <TemplateRouterLink to={auth.isLoggedIn ? accountRoute : (header.ctaUrl || '/sign-in')} onClick={handleMobileNavigation} style={{ marginTop: 12, background: ctaStyle.background, color: ctaStyle.color, border: ctaStyle.border, fontWeight: 800, borderRadius: 8, padding: '10px 12px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <FaUser size={16} />
              {auth.isLoggedIn ? 'Profile' : header.ctaLabel || header.signInLabel || 'Sign In'}
            </TemplateRouterLink>
            )}
            </div>
          </aside>
        </div>
      )}
      <style>{`
        @media (max-width: 900px) {
          .education-template-header-nav,
          .education-template-header-search,
          .education-template-header-actions {
            display: none !important;
          }
          .education-template-mobile-menu-button {
            display: grid !important;
          }
          .education-template-mobile-menu {
            display: block !important;
          }
        }
      `}</style>
    </header>
    {fixedHeader && !transparentHeader && <div aria-hidden="true" style={{ height: headerHeight }} />}
    </>
  );
};

export const ConfigurableEducationFooter = ({ template: providedTemplate, fallback = true }) => {
  const siteContext = useSiteContext();
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveEducationTemplate(siteContext);
  const template = providedTemplate || storedTemplate;
  const footer = template?.attributes?.footer;
  const theme = themeOf(template);

  // Exam template: use the Exam Portal's own footer on EVERY education route, ahead of the
  // legacy fallbacks for the same reason as the header above.
  if (isEducationExamTemplate(template)) {
    return <ExamChromeFooter template={template} company={companyState} />;
  }

  if (!footer) {
    return fallback ? <EducationDomainFooter /> : null;
  }
  if (footer.visible === false) {
    return fallback ? <EducationDomainFooter /> : null;
  }
  if (footer.mode === 'legacy' || (template?.attributes?.runtime?.renderMode === 'legacy-parity' && footer.mode !== 'custom')) {
    return fallback ? <EducationDomainFooter /> : null;
  }

  const quickLinks = Array.isArray(footer.quickLinks) ? footer.quickLinks.filter((item) => item.visible !== false) : [];
  const socialLinks = Array.isArray(footer.socialLinks) ? footer.socialLinks.filter((item) => item.visible !== false) : [];
  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const legalLinks = getCompanyPolicyLinks(companyContext);
  const logo = assetUrl(footer.poweredByLogoContent, footer.poweredByLogoUrl);
  // Task 9.3: additive footer customization (typography / columns / editable links).
  // An UNSET field keeps today's look (non-regression).
  const footerTypography = footer.typography && typeof footer.typography === 'object' ? footer.typography : {};
  const footerTypographyStyle = {
    ...(footerTypography.textAlign ? { textAlign: footerTypography.textAlign } : {}),
    ...(footerTypography.fontSize != null && footerTypography.fontSize !== '' ? { fontSize: footerTypography.fontSize } : {}),
    ...(footerTypography.fontWeight != null && footerTypography.fontWeight !== '' ? { fontWeight: footerTypography.fontWeight } : {}),
    ...(footerTypography.letterSpacing != null && footerTypography.letterSpacing !== '' ? { letterSpacing: footerTypography.letterSpacing } : {})
  };
  const editableFooterLinks = Array.isArray(footer.links)
    ? footer.links.filter((item) => item && item.visible !== false)
    : [];
  const footerColumnsOverride = Number(footer.columns);
  const footerColumns = Number.isFinite(footerColumnsOverride) && footerColumnsOverride > 0
    ? footerColumnsOverride
    : (legalLinks.length ? 4 : 3);
  const tokens = resolveFooterTokens(theme, template?.attributes?.footer);
  const headingColor = tokens.headingColor || '#1f2937';
  const linkColor = tokens.linkColor || tokens.textColor || '#374151';

  return (
    <footer id="education-template-footer" style={{ background: tokens.backgroundColor, padding: '24px 0', color: tokens.textColor, fontFamily: tokens.fontFamily, ...(footer.minHeight ? { minHeight: footer.minHeight } : {}), ...footerTypographyStyle }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 16px' }}>
        <div id="education-template-footer-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${footerColumns}, minmax(0, 1fr))`, gap: 16 }}>
          <div>
            <h6 style={{ margin: '0 0 8px', fontSize: 18, color: headingColor }}>{footer.socialTitle || 'Follow Us'}</h6>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {socialLinks.map((item) => {
                const Icon = educationSocialIconMap[String(item.icon || item.label || '').toLowerCase()] || Share2;
                return (
                  <TemplateRouterLink
                    key={item.label}
                    to={item.route || '#'}
                    aria-label={item.ariaLabel || item.label}
                    title={item.label}
                    style={{ width: 40, height: 40, borderRadius: 999, display: 'inline-grid', placeItems: 'center', color: tokens.linkColor || theme.secondary || '#746EAC', background: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}
                  >
                    <Icon size={22} />
                  </TemplateRouterLink>
                );
              })}
            </div>
          </div>
          <div>
            <h6 style={{ margin: '0 0 8px', fontSize: 18, color: headingColor }}>{footer.quickLinksTitle || 'Quick Links'}</h6>
            {quickLinks.map((item) => <TemplateRouterLink key={item.label} to={item.route || '#'} style={{ display: 'block', color: linkColor, marginBottom: 8 }}>{item.label}</TemplateRouterLink>)}
          </div>
          <div>
            <h6 style={{ margin: '0 0 8px', fontSize: 18, color: headingColor }}>{footer.contactTitle || 'Contact'}</h6>
            <div>{footer.contact?.phone || '+1 244-5333-7783'}</div>
            <a href={`mailto:${footer.contact?.email || 'avpstech.com'}`} style={{ color: linkColor }}>{footer.contact?.email || 'avpstech.com'}</a>
          </div>
          {legalLinks.length > 0 && (
            <div>
              <h6 style={{ margin: '0 0 8px', fontSize: 18, color: headingColor }}>{footer.legalTitle || 'Legal'}</h6>
              {legalLinks.map((item) => <TemplateRouterLink key={item.label} to={item.route || '#'} style={{ display: 'block', color: linkColor, marginBottom: 8 }}>{item.label}</TemplateRouterLink>)}
            </div>
          )}
          {editableFooterLinks.length > 0 && (
            <div>
              <h6 style={{ margin: '0 0 8px', fontSize: 18, color: headingColor }}>{footer.linksTitle || 'Links'}</h6>
              {editableFooterLinks.map((item, index) => (
                <TemplateRouterLink key={`${item.label || 'link'}-${index}`} {...linkProps(item.link, item.route)} style={{ display: 'block', color: linkColor, marginBottom: 8 }}>{item.label}</TemplateRouterLink>
              ))}
            </div>
          )}
        </div>
        <hr style={{ margin: '16px 0', border: 0, borderTop: `1px solid ${tokens.borderColor}` }} />
        <p style={{ textAlign: 'center', color: tokens.textColor, margin: 0 }}>
          &copy; {new Date().getFullYear()} {footer.copyrightName || 'AVPS Academy'}. All rights reserved.
        </p>
        <p style={{ textAlign: 'center', color: tokens.textColor, margin: '4px 0 0' }}>
          <strong>{footer.poweredByText || 'Powered by'}</strong>{' '}
          {logo && <img src={logo} alt="Krumago" style={{ maxHeight: 24, display: 'inline-block', verticalAlign: 'middle', marginLeft: 4 }} />}
          <br />
          <TemplateRouterLink to={footer.poweredByUrl || 'https://www.krumago.com'} style={{ color: linkColor }}>{footer.poweredByLabel || 'www.krumago.com'}</TemplateRouterLink>
        </p>
      </div>
      <style>{`
        @media (max-width: 900px) {
          #education-template-footer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
};
